import { AfterViewInit, Component, EventEmitter, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
  captureConsentScreenshot,
  openConsentScreenshotTab,
  DEBUG_SCREENSHOT_NEW_TAB
} from 'src/app/shared/consent-screenshot';

// the acceptance record is kept under its own key rather than in localStorageService,
// which bundles every value into one blob and clears it on logout
export const LEGAL_ACCEPTANCE_KEY = 'recuvery_legal_acceptance';

@Component({
  selector: 'app-legal-consent',
  templateUrl: './legal-consent.component.html',
  styleUrl: './legal-consent.component.scss',
})
export class LegalConsentComponent implements AfterViewInit {
  // terms & conditions are reviewed first, privacy policy second
  tab = {
    PRIVACY: 'PRIVACY',
    TERMS: 'TERMS'
  };
  activeTab = this.tab.TERMS;
  // set from the sign up form when the modal opens, so the capture records who accepted
  userEmail = '';
  // Name of the person accepting, shown in the agree line so the record says who
  // agreed rather than just that someone did. Empty falls back to the plain wording.
  accepterName = '';
  // Whether the caller is in a state where accepting can do anything. The
  // documents can always be read; accepting is only offered once there is a
  // form to submit. Defaults true so callers that do not set it are unaffected.
  canAccept = true;
  legalAccepted = false;
  // the agree checkbox lives in the modal footer and gates Accept & Continue
  agreeChecked = false;
  // the agree tick stays locked until the terms have been read to the end
  documentScrolled = false;
  // whether the open document is scrolled to its end - hides the jump-to-bottom button
  bodyAtBottom = false;
  capturing = false;

  @Output() onAccept = new EventEmitter<boolean>();

  constructor(private bsModalRef: BsModalRef) { }

  ngAfterViewInit() {
    // the terms may be short enough not to scroll at all on a tall screen
    setTimeout(() => this.onBodyScroll(), 60);
  }

  setTab(tab: string) {
    // the privacy policy stays locked until the terms have been read to the end
    if (tab === this.tab.PRIVACY && !this.documentScrolled) return;
    this.activeTab = tab;
    this.bodyAtBottom = false;
    const body = document.getElementById('legalModalBody');
    if (body) {
      body.scrollTop = 0;
    }
    // re-measure once the new document has rendered
    setTimeout(() => this.onBodyScroll(), 60);
  }

  // tracks whether the open document is at its end, and marks the terms as read once they are
  onBodyScroll() {
    const body = document.getElementById('legalModalBody');
    if (!body) return;
    this.bodyAtBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 4;
    if (this.bodyAtBottom && this.activeTab === this.tab.TERMS) {
      this.documentScrolled = true;
    }
  }

  // jumps straight to the end of the open document
  scrollToBottom() {
    const body = document.getElementById('legalModalBody');
    if (!body) return;
    body.scrollTop = body.scrollHeight;
    this.onBodyScroll();
  }

  next() {
    this.setTab(this.tab.PRIVACY);
  }

  // accepts both documents together, from the privacy tab
  async accept() {
    if (this.legalAccepted || !this.documentScrolled || !this.agreeChecked || this.capturing) return;

    // record what the user actually saw before the modal leaves the DOM
    await this.captureAcceptance();

    this.legalAccepted = true;
    this.onAccept.emit(true);
    this.onClose();
  }

  onClose() {
    this.bsModalRef.hide();
  }

  // one capture of the whole screen as it looked when Accept & Continue was clicked.
  // a capture failure never blocks the sign up.
  private async captureAcceptance() {
    this.capturing = true;
    try {
      // let the ticked checkbox paint before the screen is captured
      await new Promise(resolve => setTimeout(resolve, 60));

      // the dialog alone is the record: email, documents, ticked box and buttons
      const modal = document.querySelector<HTMLElement>('.legal-modal');
      const screenshot = await captureConsentScreenshot(modal, {
        label: 'legal_acceptance',
        // as displayed — scrolled to the end of the terms, box ticked
        viewport: true,
        mime: 'image/jpeg',
        quality: 0.8
      });
      if (DEBUG_SCREENSHOT_NEW_TAB) openConsentScreenshotTab(screenshot);

      const record = {
        accepted_at: new Date().toISOString(),
        user_agent: navigator.userAgent,
        screenshot: screenshot || ''
      };
      const payload = JSON.stringify(record);
      localStorage.setItem(LEGAL_ACCEPTANCE_KEY, payload);
      console.log(
        `[legal] acceptance stored in localStorage["${LEGAL_ACCEPTANCE_KEY}"] ` +
        `(${Math.round(payload.length / 1024)} KB)`, record
      );
    } catch (err) {
      console.error('[legal] acceptance capture failed', err);
    }
    this.capturing = false;
  }

  /** the stored capture, for sending with the register payload */
  static storedAcceptanceCapture(): string | null {
    try {
      const raw = localStorage.getItem(LEGAL_ACCEPTANCE_KEY);
      if (!raw) return null;
      const record = JSON.parse(raw);
      return (record && record.screenshot) ? record.screenshot : null;
    } catch (error) {
      console.error('[legal] could not read the stored acceptance', error);
      return null;
    }
  }
}
